import { Button } from "@gigpro/ui/Button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@gigpro/ui/Collapsible";
import { Calendar, ChevronDown, Clock } from "@gigpro/ui/Icons";
import { Separator } from "@gigpro/ui/Separator";
import { pluralize } from "@gigpro/utils";
import { formatDate, formatTime, shortenTimezone } from "@gigpro/utils/datetime";
import type { OfferableGig } from "api/resources/gigs";
import { HighDemandChip } from "components/HighDemandChip";
import {
  aggregateGroupHighDemandSurcharge,
  type MemberSurchargeState,
} from "domains/pros/hooks/aggregateGroupHighDemandSurcharge";
import React from "react";
import { times } from "remeda";
import { areGigsOverlapping } from "utils/GigUtil";

type OfferExistingGigProps = {
  gigsBySkill: Array<[string, Array<OfferableGig>]>;
  onOfferGig: (gigId: number) => Promise<any>;
  /**
   * Display name of the single pro the offer is being sent to. When provided
   * alongside a `surchargeMultiplier`, high-demand shift rows surface a chip
   * advising that selecting this pro will freeze the multiplier at send time.
   * Mutually exclusive with `group` — group offers use that instead.
   */
  proName?: string;
  /**
   * The active location's `high_demand_surcharge_multiplier`. The picker filters
   * results to one location, so a single multiplier covers every row.
   */
  surchargeMultiplier?: number;
  /**
   * Group target for the offer. Triggers the group chip/warning rule on each
   * row: 100% of members incur surcharge → chip, partial → "X of N members
   * will incur surcharge" warning, 0% → nothing. Mutually exclusive with
   * `proName`.
   */
  group?: { name: string; membersCount: number };
};

// Selection state takes precedence over the predicate: once a shift has been
// offered, the chip reflects the multiplier frozen at send time. Admin-selected
// rows freeze as null and render no chip. Unselected rows fall back to the
// predicate + the location's current multiplier.
const resolveEffectiveMultiplier = (gig: OfferableGig, locationMultiplier: number | undefined): number | null => {
  if (gig.is_selected) return gig.high_demand_surcharge_multiplier_applied;
  if (gig.is_high_demand) return locationMultiplier ?? null;
  return null;
};

// Until the shift-list endpoint exposes per-member application state, the
// shift-level signals (`is_selected`, `high_demand_surcharge_multiplier_applied`,
// `is_high_demand`) are computed against a single pro and treated as a
// homogeneous proxy for every member. Aggregation collapses to 100%/0% with
// today's data; partial states light up automatically when per-member fields
// arrive without touching the dispatch logic.
const buildHomogeneousMemberStates = (gig: OfferableGig, membersCount: number): Array<MemberSurchargeState> =>
  times(membersCount, () => ({
    hasSelectedApp: gig.is_selected ?? false,
    frozenMultiplier: gig.high_demand_surcharge_multiplier_applied,
    isPredicateHighDemand: gig.is_high_demand,
  }));

export const OfferExistingGig = ({
  gigsBySkill,
  onOfferGig,
  proName,
  surchargeMultiplier,
  group,
}: OfferExistingGigProps) => {
  const [loadingGig, setLoadingGig] = React.useState<number>();

  // Gating (LD flag + location toggle) is enforced upstream — the parent
  // passes a nullable `surchargeMultiplier` whose presence is the contract
  // for "high-demand surcharges are active for this location".
  const locationMultiplier = surchargeMultiplier;

  const allGigs = gigsBySkill.flatMap(([, gigs]) => gigs);

  const emptyMessage = group
    ? "We couldn't find any shifts to offer this group"
    : "We couldn't find any shifts to offer this Pro";

  return gigsBySkill.length > 0 ? (
    <div className="flex flex-col gap-2 sm:w-96">
      {gigsBySkill.map(([skill, gigs], index) => (
        <Collapsible key={skill} defaultOpen={index === 0}>
          <CollapsibleTrigger className="mb-1 flex items-center gap-1 text-large font-semibold [&>svg]:data-[state=open]:rotate-180">
            {skill}
            <ChevronDown className="size-4 text-brand transition-transform" />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="grid max-h-80 grid-cols-[min-content_2fr_3fr_min-content] gap-1 overflow-auto pr-1">
              {gigs.map((gig) => {
                const isAnyGigLoading = loadingGig != null;
                const isThisGigLoading = loadingGig === gig.id;
                const isBusy =
                  (!isThisGigLoading && gig.is_filled) ||
                  (!gig.is_selected &&
                    allGigs.some(
                      (compareGig) =>
                        gig.id !== compareGig.id && compareGig.is_confirmed && areGigsOverlapping(gig, compareGig),
                    ));

                const isSent = gig.is_selected;

                const isOfferable = !isSent && !isBusy && !isAnyGigLoading && !gig.is_filled;

                let buttonLabel: string;
                if (isBusy) {
                  buttonLabel = "Busy";
                } else if (isSent) {
                  buttonLabel = "Sent";
                } else if (gig.is_filled) {
                  buttonLabel = "Filled";
                } else {
                  buttonLabel = "Offer";
                }

                return (
                  <div
                    key={gig.id}
                    className="col-start-1 -col-end-1 grid grid-cols-[min-content_2fr_3fr_min-content] items-center justify-between gap-0.5 text-normal tracking-tight supports-[grid-template-columns:subgrid]:grid-cols-subgrid sm:gap-1"
                    data-testid={`gigrow_${gig.id}`}
                  >
                    <Calendar className="size-3.5 text-gray" />
                    {formatDate(gig.start_time, "EEE, MMM d")}
                    <div className="flex items-center gap-1 whitespace-nowrap">
                      <Clock className="size-3.5 text-gray" />
                      {formatTime(gig.start_time)} - {formatTime(gig.end_time)}
                      <RowChipSlot gig={gig} proName={proName} group={group} locationMultiplier={locationMultiplier} />
                    </div>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={async () => {
                        setLoadingGig(gig.id);
                        await onOfferGig(gig.id);
                        setLoadingGig(undefined);
                      }}
                      disabled={!isOfferable}
                      loading={isThisGigLoading}
                      className="px-1.5 sm:px-2"
                      data-testid={`button_offergig_${gig.id}`}
                    >
                      {buttonLabel}
                    </Button>
                  </div>
                );
              })}
            </div>
            {index + 1 < gigsBySkill.length && <Separator className="mt-4 mb-0 bg-light-gray" />}
          </CollapsibleContent>
        </Collapsible>
      ))}
    </div>
  ) : (
    <p className="mt-2 text-center text-normal font-semibold">{emptyMessage}</p>
  );
};

type RowChipSlotProps = {
  gig: OfferableGig;
  proName: string | undefined;
  group: { name: string; membersCount: number } | undefined;
  locationMultiplier: number | undefined;
};

const RowChipSlot: React.FC<RowChipSlotProps> = ({ gig, proName, group, locationMultiplier }) => {
  if (group) {
    const outcome = aggregateGroupHighDemandSurcharge({
      members: buildHomogeneousMemberStates(gig, group.membersCount),
      baseWage: gig.wage_amount,
      locationMultiplier,
    });
    if (outcome?.type === "chip") {
      return (
        <HighDemandChip
          proName={group.name}
          baseWage={outcome.baseWage}
          multiplier={outcome.multiplier}
          compact
          className="px-1.5 py-0 text-small"
          tooltipContent={`Every member of ${group.name} will bill at the High-demand rate (${outcome.multiplier}× this shift's wage). Pick a group with non-high-demand pros to avoid the surcharge.`}
        />
      );
    }
    if (outcome?.type === "warning") {
      const noun = pluralize(outcome.incurringCount, "member");
      return (
        <HighDemandChip
          proName={group.name}
          baseWage={outcome.baseWage}
          multiplier={outcome.multiplier}
          compact
          className="px-1.5 py-0 text-small"
          tooltipContent={`${outcome.incurringCount} of ${outcome.totalCount} ${noun} in ${group.name} will bill at the High-demand rate (${outcome.multiplier}× this shift's wage). Pick a different group to avoid the surcharge.`}
        />
      );
    }
    return <span className="text-small text-gray">({shortenTimezone(gig.location.timezone)})</span>;
  }

  const proMultiplier = resolveEffectiveMultiplier(gig, locationMultiplier);
  if (proName && proMultiplier != null && gig.wage_amount !== null) {
    return (
      <HighDemandChip
        proName={proName}
        baseWage={gig.wage_amount}
        multiplier={proMultiplier}
        compact
        className="px-1.5 py-0 text-small"
      />
    );
  }
  return <span className="text-small text-gray">({shortenTimezone(gig.location.timezone)})</span>;
};
